import { useContext, useEffect, useState } from 'react';
import { TabValue } from '@fluentui/react-components';

import DocumentContext from '../contexts/DocumentContext.context';
import { MenuProvider as LinkMenuProvider } from '../contexts/MenuContext.context';
import useEmbeddedEditMode from '../hooks/useEmbeddedEditMode.hook';
import Content from './Content';
import DocumentRibbon from './header/DocumentRibbon';
import DocumentSaveStatus from './header/DocumentSaveStatus';
import { AUTO_EDIT_QUERY_PARAM, AUTO_EDIT_QUERY_VALUE, EItemScreenMode, useItemScreenStore } from '@modules/items';
import { useBlockCtrlS, useSearchParams } from '@shared/hooks';

/*
 * The document editor for a page that draws its own chrome; edit mode belongs to the page (useEmbeddedEditMode).
 * Each shown document gets its own keyed DocumentProvider, which owns its save and lock, and each section is a child
 * route, because the leave guard blocks only path changes.
 */
const EmbeddedDocument = () => {
  const { isEdit, requestEdit, isAutoEditPending } = useContext(DocumentContext);
  const { autoEdit } = useEmbeddedEditMode();
  const { change } = useSearchParams();
  const [selectedTab, setSelectedTab] = useState<TabValue>('home');
  const { screenMode, viewChanges, setScreenMode } = useItemScreenStore();

  useBlockCtrlS();

  // Starting to edit drops `autoEdit`; restore it so edit mode survives a document someone else holds.
  const connecting = requestEdit && !isEdit;
  useEffect(() => {
    if (connecting && !autoEdit) change(AUTO_EDIT_QUERY_PARAM, AUTO_EDIT_QUERY_VALUE);
  }, [connecting, autoEdit]);

  // The screen store is global, and this page has no button to close a Changes view or an impact analysis.
  useEffect(() => {
    if (viewChanges || screenMode === EItemScreenMode.PRE_CHANGE_IMPACT_ANALYSIS) setScreenMode();
  }, [viewChanges, screenMode]);

  if (viewChanges) return null;

  return (
    <LinkMenuProvider>
      <div className="flex min-h-0 flex-1 flex-col">
        {/* In edit mode the next document gets the ribbon with its paper, or the paper drops when it connects. */}
        {(isEdit || requestEdit || isAutoEditPending) && (
          <DocumentRibbon
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            actions={<DocumentSaveStatus />}
            appearance="card"
          />
        )}
        {/* The paper scrolls inside its own box, which needs a set height, and is centred under the ribbon. */}
        <div className="mx-auto min-h-0 w-fit flex-1">
          {/* Its document may get a draft only once it is edited, while the rail already says Edit draft. */}
          <Content editActionLabel="Edit draft" />
        </div>
      </div>
    </LinkMenuProvider>
  );
};

export default EmbeddedDocument;
